import { useState, useEffect, useCallback } from "react";
import { DateField } from "@/components/ui/date-field";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import useHiddenModules from "@/lib/useHiddenModules";
import { useOpenTaskCounts, OpenTasksBadge } from "@/components/OpenTasksBadge";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Building2, Users, MapPin, ArrowRight, Briefcase, Pencil, Plus, Phone, StickyNote, Trash2, MessageSquare, FileText, MessageCircle, Mail, Archive, PenLine } from "lucide-react";
import { DeliveryBadge } from "@/components/DeliveryBadge";
import { fmtEur, fmtDate, photoSrc, COLOR, COLOR_LABEL, DOT } from "@/lib/format";
import { Combobox } from "@/components/ui/combobox";
import ClientWizard from "@/components/wizards/ClientWizard";
import PropertyWizard from "@/components/wizards/PropertyWizard";
import QuickMessageDialog from "@/components/QuickMessageDialog";
import { ClientAgreements } from "@/components/ClientAgreements";
import { DocumentsTab } from "@/components/DocumentsTab";
import DossierExportDialog from "@/components/DossierExportDialog";
import { AddressMapButton } from "@/components/AddressMapButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const TYPES = [["telefoon", "Telefoon"], ["email", "E-mail"], ["notitie", "Notitie"], ["huisbezoek", "Bezoek"]];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const hidden = useHiddenModules();
  const noPay = hidden.includes("tab-betalingen");
  const taskCounts = useOpenTaskCounts(noPay);
  const [data, setData] = useState(null);
  const [editClient, setEditClient] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [logs, setLogs] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [note, setNote] = useState({ date: new Date().toISOString().slice(0, 10), type: "telefoon", outcome: "", followup_date: "" });
  const [msgOpen, setMsgOpen] = useState(false);
  const [dossierOpen, setDossierOpen] = useState(false);

  const load = useCallback(() => api.get(`/clients/${id}/overview`).then((r) => setData(r.data)).catch(() => navigate("/app/opdrachtgevers")), [id, navigate]);
  const archiveClient = async () => {
    try { await api.patch(`/clients/${id}/archive`, { value: true }); toast.success("Opdrachtgever gearchiveerd"); navigate("/app/opdrachtgevers"); }
    catch { toast.error("Archiveren mislukt"); }
  };
  const loadLogs = useCallback(() => api.get(`/clients/${id}/calllogs`).then((r) => setLogs(r.data)).catch(() => {}), [id]);
  const loadTimeline = useCallback(() => api.get(`/clients/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {}), [id]);
  const [msgTpls, setMsgTpls] = useState([]);
  const dlDossier = async () => {
    try {
      const res = await api.get(`/clients/${id}/communication/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `communicatie_${id}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Dossier genereren mislukt"); }
  };
  useEffect(() => { load(); loadLogs(); loadTimeline(); api.get("/catalog").then((r) => setMsgTpls(r.data.message_templates || [])).catch(() => {}); }, [load, loadLogs, loadTimeline]);

  const addNote = async () => {
    if (!note.outcome.trim()) return toast.error("Vul een notitie / uitkomst in");
    await api.post("/calllogs", { client_id: id, ...note });
    toast.success("Toegevoegd aan communicatie");
    setNote({ date: new Date().toISOString().slice(0, 10), type: "telefoon", outcome: "", followup_date: "" });
    loadLogs(); loadTimeline();
  };
  const delNote = async (nid) => { await api.delete(`/calllogs/${nid}`); loadLogs(); loadTimeline(); };

  if (!data) return <div className="py-20 text-center text-slate-400">Laden...</div>;
  const c = data.client;
  const clientContacts = () => {
    const arr = [];
    if (c.contact_person || c.phone || c.email) arr.push({ name: c.contact_person || c.name, role: ["Primair contactpersoon", c.contact_function].filter(Boolean).join(" · "), phone: c.phone, email: c.email });
    if (c.contact_person2 || c.phone2 || c.email2) arr.push({ name: c.contact_person2 || "Secundair contact", role: ["Secundair contactpersoon", c.contact2_function].filter(Boolean).join(" · "), phone: c.phone2, email: c.email2 });
    (c.contacts || []).forEach((ct) => (ct.name || ct.phone || ct.email) && arr.push({ name: ct.name || "Contact", role: ct.function || "Contactpersoon", phone: ct.phone, email: ct.email }));
    if (c.billing_phone || c.billing_email) arr.push({ name: c.name, role: "Facturatie", phone: c.billing_phone, email: c.billing_email });
    (c.asset_managers || []).forEach((m) => (m.name || m.mobile || m.phone || m.email) && arr.push({ name: m.name, role: "Assetmanager", phone: m.mobile || m.phone, email: m.email }));
    (c.account_managers || []).forEach((m) => (m.name || m.mobile || m.phone || m.email) && arr.push({ name: m.name, role: "Accountmanager", phone: m.mobile || m.phone, email: m.email }));
    return arr;
  };
  const DUNNING = { herinnering_1: "1e herinnering", herinnering_2: "2e herinnering", aanmaning: "Aanmaning" };
  const statusCounts = {}; const dunningCounts = {};
  (data.tenants || []).forEach((t) => {
    (t.statuses || []).forEach((s) => { statusCounts[s] = (statusCounts[s] || 0) + 1; });
    if (t.dunning_stage) dunningCounts[t.dunning_stage] = (dunningCounts[t.dunning_stage] || 0) + 1;
  });

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/app/opdrachtgevers")} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800" data-testid="back-to-clients">
        <ArrowLeft className="h-4 w-4" /> Terug naar opdrachtgevers
      </button>

      <Card className="p-6">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <div className="h-20 w-20 rounded-xl border bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
            {c.photo_url ? <img src={photoSrc(c.photo_url)} alt="" className="h-full w-full object-cover" /> : <Briefcase className="h-8 w-8 text-slate-300" />}
          </div>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-heading text-2xl font-bold text-[#0F1E3D]">{c.name}</h1>
            <p className="text-slate-500 text-sm capitalize">{c.type} {c.contact_person && `• ${c.contact_person}`}</p>
            <div className="flex flex-wrap gap-3 text-xs text-slate-500 mt-1">
              {c.email && <span>{c.email}</span>}{c.phone && <span>· {c.phone}</span>}{c.address && <span>· {c.address} <AddressMapButton address={`${c.address}, ${c.postal_code || ""} ${c.city || ""}`} /></span>}
            </div>
            {!noPay && <Badge className={`${COLOR[c.status_color]} mt-2`}>{COLOR_LABEL[c.status_color]}</Badge>}
          </div>
          <div className="flex items-center gap-x-6 gap-y-3 flex-wrap md:justify-end md:max-w-[60%]">
            {!noPay && <div className="text-right"><div className="text-xs text-slate-400">Openstaand</div><div className="text-xl font-bold text-slate-800">{fmtEur(c.outstanding)}</div></div>}
            {!noPay && <div className="text-right"><div className="text-xs text-slate-400">Te laat</div><div className={`text-xl font-bold ${c.overdue_amount > 0 ? "text-red-600" : "text-slate-800"}`}>{fmtEur(c.overdue_amount)}</div></div>}
            {(c.phone || c.billing_phone || c.email || c.billing_email) && <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => setMsgOpen(true)} data-testid="client-message-button"><MessageCircle className="h-4 w-4 mr-2" /> Bericht</Button>}
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => navigate(`/app/instellingen?tab=brieven&client=${id}`)} data-testid="client-templates-button"><FileText className="h-4 w-4 mr-2" /> Briefsjablonen</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => navigate(`/app/brievengenerator?client=${id}`)} data-testid="client-letter-generator-button"><PenLine className="h-4 w-4 mr-2" /> Brievengenerator</Button>
            <Button className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white text-white" onClick={() => setEditClient(true)} data-testid="edit-client-button"><Pencil className="h-4 w-4 mr-2" /> Bewerken</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="archive-client-button"><Archive className="h-4 w-4 mr-2" /> Archiveren</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Opdrachtgever archiveren?</AlertDialogTitle>
                  <AlertDialogDescription>Deze opdrachtgever verdwijnt uit de actieve lijsten en wordt bewaard in het centrale archief. Bijbehorende panden en debiteuren blijven gewoon staan. U kunt hem later terugzetten of definitief verwijderen.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuleren</AlertDialogCancel>
                  <AlertDialogAction className="bg-slate-700 hover:bg-slate-800" onClick={archiveClient} data-testid="archive-client-confirm">Ja, archiveren</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
        {c.notes && <div className="mt-4 text-sm text-slate-600 bg-slate-50 rounded-lg p-3"><b className="text-slate-500">Notitie:</b> {c.notes}</div>}
      </Card>

      <Tabs defaultValue="overzicht" className="w-full">
        <TabsList data-testid="client-tabs">
          <TabsTrigger value="overzicht" data-testid="client-tab-overzicht" className="data-[state=active]:bg-slate-800 data-[state=active]:text-white"><Briefcase className="h-4 w-4 mr-1.5" /> Overzicht</TabsTrigger>
          <TabsTrigger value="communicatie" data-testid="client-tab-communicatie" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white"><MessageSquare className="h-4 w-4 mr-1.5" /> Communicatie</TabsTrigger>
          <TabsTrigger value="tijdslijn" data-testid="client-tab-tijdslijn" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white"><StickyNote className="h-4 w-4 mr-1.5" /> Tijdslijn</TabsTrigger>
          <TabsTrigger value="documenten" data-testid="client-tab-documenten" className="data-[state=active]:bg-[#0F1E3D] data-[state=active]:text-white"><FileText className="h-4 w-4 mr-1.5" /> Documenten</TabsTrigger>
        </TabsList>

        <TabsContent value="overzicht" className="space-y-6 mt-4">
      <Card className="p-5 cursor-pointer transition-colors hover:border-blue-300 hover:bg-blue-50/30 group" data-testid="client-stamgegevens" onClick={() => setEditClient(true)} title="Klik om te bewerken">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><Briefcase className="h-4 w-4" /> Stamgegevens <Pencil className="h-3.5 w-3.5 text-slate-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" /></h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          {c.client_number && <div><div className="text-xs text-slate-400">Klantnummer</div><div className="font-medium text-slate-800">{c.client_number}</div></div>}
          {c.contact_person && <div><div className="text-xs text-slate-400">Primair contactpersoon</div><div className="font-medium text-slate-800">{c.contact_person}</div></div>}
          {c.contact_person2 && <div><div className="text-xs text-slate-400">Secundair contactpersoon</div><div className="font-medium text-slate-800">{c.contact_person2}</div></div>}
          {(c.postal_code || c.city) && <div><div className="text-xs text-slate-400">Postcode / plaats</div><div className="font-medium text-slate-800">{c.postal_code} {c.city}</div></div>}
          {c.rate && <div><div className="text-xs text-slate-400">Tarief</div><div className="font-medium text-slate-800">{c.rate}</div></div>}
        </div>

        {(c.billing_address || c.billing_email || c.billing_phone || c.special_terms) && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs font-semibold text-slate-500 mb-2">Facturatiegegevens</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {c.billing_address && <div><div className="text-xs text-slate-400">Correspondentieadres</div><div className="text-slate-800">{c.billing_address}</div></div>}
              {c.billing_email && <div><div className="text-xs text-slate-400">E-mail</div><div className="text-slate-800">{c.billing_email}</div></div>}
              {c.billing_phone && <div><div className="text-xs text-slate-400">Telefoon</div><div className="text-slate-800">{c.billing_phone}</div></div>}
              {c.special_terms && <div className="sm:col-span-2 lg:col-span-3"><div className="text-xs text-slate-400">Bijzondere afspraken</div><div className="text-slate-800 whitespace-pre-wrap">{c.special_terms}</div></div>}
            </div>
          </div>
        )}

        {(c.contacts || []).filter((ct) => ct.name || ct.phone || ct.email || ct.mobile).length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs font-semibold text-slate-500 mb-2">Contactpersonen</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {c.contacts.filter((ct) => ct.name || ct.phone || ct.email || ct.mobile).map((ct, i) => (
                <div key={i} className="border rounded-lg p-2.5 text-sm" data-testid={`client-contact-${i}`}>
                  <div className="font-medium text-slate-800">{ct.name || "—"}{ct.function && <span className="text-xs font-normal text-slate-400"> · {ct.function}</span>}</div>
                  <div className="text-xs text-slate-500">{[ct.mobile, ct.phone, ct.email].filter(Boolean).join(" · ")}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {[["asset_managers", "Assetmanagers"], ["account_managers", "Accountmanagers"]].map(([key, label]) => (
          (c[key] || []).length > 0 && (
            <div key={key} className="mt-4 pt-4 border-t">
              <div className="text-xs font-semibold text-slate-500 mb-2">{label}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {c[key].map((m, i) => (
                  <div key={i} className="border rounded-lg p-2.5 text-sm" data-testid={`client-${key}-${i}`}>
                    <div className="font-medium text-slate-800">{m.name || "—"}</div>
                    <div className="text-xs text-slate-500">{[m.mobile, m.phone, m.email].filter(Boolean).join(" · ")}</div>
                  </div>
                ))}
              </div>
            </div>
          )
        ))}

        {(c.bank_accounts || []).length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs font-semibold text-slate-500 mb-2">Bankrekeningen</div>
            <div className="space-y-1">
              {c.bank_accounts.map((b, i) => (
                <div key={i} className="text-sm text-slate-800 flex gap-2" data-testid={`client-bank-${i}`}><span className="font-mono">{b.iban || "—"}</span>{b.name_tag && <span className="text-slate-400">· {b.name_tag}</span>}</div>
              ))}
            </div>
          </div>
        )}

        {(c.custom_fields || []).filter((f) => f.label || f.value).length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-xs font-semibold text-slate-500 mb-2">Extra velden</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
              {c.custom_fields.filter((f) => f.label || f.value).map((f, i) => (
                <div key={i} data-testid={`client-custom-${i}`}><div className="text-xs text-slate-400">{f.label || "—"}</div><div className="text-slate-800">{f.value}</div></div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {!hidden.includes("contracts") && <ClientAgreements clientId={id} />}

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold flex items-center gap-2"><Building2 className="h-4 w-4" /> Panden ({data.properties.length})</h3>
          <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={() => setEditProp({ client_id: id })} data-testid="add-property-inline-button"><Plus className="h-4 w-4 mr-1" /> Pand toevoegen</Button>
        </div>
        <div className="space-y-2">
          {data.properties.length === 0 && <p className="text-sm text-slate-400">Nog geen panden.</p>}
          {data.properties.map((p) => (
            <div key={p.id} onClick={() => setEditProp(p)} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 hover:border-slate-300 cursor-pointer transition-colors" data-testid={`client-property-${p.id}`}>
              <div className="flex items-center gap-3">
                {p.photo_url ? <img src={photoSrc(p.photo_url)} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center"><MapPin className="h-4 w-4 text-slate-400" /></div>}
                <div><div className="font-medium text-slate-800">{p.street} {p.number} <AddressMapButton address={`${p.street} ${p.number}, ${p.postal_code} ${p.city}`} /></div><div className="text-xs text-slate-500">{p.postal_code} {p.city} • {p.type}</div></div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant="secondary">{p.tenant_count} debiteur(en)</Badge>
                <Pencil className="h-4 w-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading font-semibold flex items-center gap-2"><Users className="h-4 w-4" /> Debiteuren ({data.tenants.length})</h3>
          {(Object.keys(statusCounts).length > 0 || Object.keys(dunningCounts).length > 0) && (
            <div className="flex flex-wrap gap-1.5" data-testid="client-status-counts">
              {Object.entries(dunningCounts).map(([k, n]) => <Badge key={k} className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[11px]">{DUNNING[k] || k}: {n}</Badge>)}
              {Object.entries(statusCounts).map(([s, n]) => <Badge key={s} variant="outline" className="border-blue-200 text-blue-700 text-[11px]">{s}: {n}</Badge>)}
            </div>
          )}
        </div>
        <div className="space-y-2">
          {data.tenants.length === 0 && <p className="text-sm text-slate-400">Nog geen debiteuren.</p>}
          {data.tenants.map((t) => (
            <div key={t.id} onClick={() => navigate(`/app/huurders/${t.id}`)} className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer" data-testid={`client-tenant-${t.id}`}>
              <div className="flex items-center gap-3">
                {!noPay && <span className={`h-2.5 w-2.5 rounded-full ${DOT[t.status_color]}`} />}
                <div>
                  <div className="font-medium text-slate-800">{t.first_name} {t.last_name}</div>
                  <div className="text-xs text-slate-500">{t.property_label || t.naw_city}</div>
                  {(t.dunning_stage || (t.statuses || []).length > 0) && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {t.dunning_stage && <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-100 text-[10px]">{DUNNING[t.dunning_stage] || t.dunning_stage}</Badge>}
                      {(t.statuses || []).map((s) => <Badge key={s} variant="outline" className="border-blue-200 text-blue-700 text-[10px]">{s}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                {noPay && <OpenTasksBadge n={taskCounts[t.id] || 0} tid={t.id} />}
                {!noPay && <div className="text-right"><div className="text-xs text-slate-400">Openstaand</div><div className="font-semibold">{fmtEur(t.outstanding)}</div></div>}
                {!noPay && <div className="text-right"><div className="text-xs text-slate-400">Te laat</div><div className={`font-semibold ${t.overdue_amount > 0 ? "text-red-600" : ""}`}>{fmtEur(t.overdue_amount)}</div></div>}
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </div>
            </div>
          ))}
        </div>
      </Card>

        </TabsContent>

        <TabsContent value="communicatie" className="mt-4">
      <Card className="p-5">
        <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> Communicatie & notities</h3>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div><Label>Datum</Label><DateField testId="client-note-date" value={note.date} onChange={(v) => setNote({ ...note, date: v })} /></div>
          <div>
            <Label>Type</Label>
            <Combobox testId="client-note-type" value={note.type} onChange={(v) => setNote({ ...note, type: v })} options={TYPES.map(([v, l]) => ({ value: v, label: l }))} />
          </div>
          <div className="flex items-end"><Button className="w-full bg-[#0F1E3D] hover:bg-[#1B2D52] text-white" onClick={addNote} data-testid="client-note-submit"><Plus className="h-4 w-4 mr-1" /> Toevoegen</Button></div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          <div><Label>Opvolgdatum (Taken)</Label><DateField testId="client-note-followup" value={note.followup_date} onChange={(v) => setNote({ ...note, followup_date: v })} /></div>
        </div>
        <Textarea rows={2} className="mb-4" placeholder="Uitkomst / notitie..." value={note.outcome} onChange={(e) => setNote({ ...note, outcome: e.target.value })} data-testid="client-note-outcome" />
        <div className="space-y-2" data-testid="client-comms-list">
          {logs.length === 0 && <p className="text-sm text-slate-400">Nog geen communicatie vastgelegd.</p>}
          {logs.map((l) => (
            <div key={l.id} className="flex items-start justify-between p-3 rounded-lg border" data-testid={`client-comm-${l.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">{l.type === "notitie" ? <StickyNote className="h-4 w-4 text-slate-500" /> : <Phone className="h-4 w-4 text-slate-500" />}</div>
                <div>
                  <div className="text-sm font-medium text-slate-800 capitalize">{l.type} • {fmtDate(l.date)}</div>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{l.outcome}</p>
                </div>
              </div>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => delNote(l.id)} data-testid={`client-comm-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="documenten" className="mt-4">
          <DocumentsTab clientId={id} onChanged={loadTimeline} />
        </TabsContent>

        <TabsContent value="tijdslijn" className="mt-4">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-semibold flex items-center gap-2"><StickyNote className="h-4 w-4" /> Communicatie-tijdslijn</h3>
          <Button size="sm" variant="outline" className="text-blue-700 border-blue-200" onClick={dlDossier} data-testid="client-communication-pdf"><FileText className="h-4 w-4 mr-2" /> Dossier (PDF)</Button>
        </div>
        {timeline.length === 0 ? <p className="text-sm text-slate-400">Nog geen activiteit vastgelegd.</p> : (
          <div className="space-y-4" data-testid="client-timeline-list">
            {timeline.map((e, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  {e.kind === "letter" ? <Mail className="h-4 w-4 text-slate-500" /> : <Phone className="h-4 w-4 text-slate-500" />}
                </div>
                <div className="flex-1 border-b pb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{e.title}</span>
                    {e.tenant_name && <Badge variant="outline" className="text-[11px] border-blue-200 text-blue-700">{e.tenant_name}</Badge>}
                    <span className="text-xs text-slate-400 ml-auto">{fmtDate(e.date)}</span>
                  </div>
                  {e.description && <p className="text-sm text-slate-500 whitespace-pre-wrap mt-0.5">{e.description}</p>}
                  {e.kind === "call" && e.delivery_status && (
                    <div className="mt-1.5"><DeliveryBadge status={e.delivery_status} channel={e.delivery_channel} testId={`client-timeline-delivery-${i}`} /></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
        </TabsContent>
      </Tabs>

      <ClientWizard open={editClient} onOpenChange={setEditClient} initial={c} onSaved={load} />
      <PropertyWizard open={!!editProp} onOpenChange={(o) => !o && setEditProp(null)} initial={editProp && editProp.id ? editProp : null} presetClient={id} onSaved={load} />
      <QuickMessageDialog open={msgOpen} onOpenChange={setMsgOpen} contacts={clientContacts()} templates={msgTpls.map((tpl) => ({ label: tpl.label, subject: (tpl.subject || "").replaceAll("{naam}", c.name).replaceAll("{bedrag}", "de openstaande bedragen"), body: (tpl.body || "").replaceAll("{naam}", c.name).replaceAll("{bedrag}", "de openstaande bedragen") }))} logPayload={{ client_id: id }} onLogged={() => { loadLogs(); loadTimeline(); }} />
      <DossierExportDialog open={dossierOpen} onOpenChange={setDossierOpen} endpoint={`/clients/${id}/communication/pdf`} filename={`communicatie_${id}.pdf`} kinds={[{ value: "call", label: "Gesprekken/notities" }, { value: "letter", label: "Brieven" }]} />
    </div>
  );
}
